'use client'

import { useCallback, useEffect, useRef, useState, type FC } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgPause from '@/icons/solid/Pause'
import SvgPlay from '@/icons/solid/Play'
import { cx } from '@/lib/utils'

import { AudioProgressRing } from '../audio-progress-ring'
import { useAudioPlayer } from '../use-audio-player'
import { useAudioReducedMotion } from '../use-audio-reduced-motion'

const BAR_COUNT = 40
const IDLE_BAR_HEIGHTS = Array<number>(BAR_COUNT).fill(20)
// Deterministic pseudo-random delays so bars animate out of phase without
// using Math.random — cacheComponents flags non-deterministic module-level
// code during SSR (this file is `'use client'` but the module still
// evaluates server-side when imported transitively through providers).
// Plain modular hash gives a visually-uniform spread across [0, 1).
const BAR_DELAYS = Array.from({ length: BAR_COUNT }, (_, i) => ((i * 2654435761) >>> 0) / 0xffffffff)

interface AudioBarsProps {
  url: string
  title?: string
  group?: string
  compact?: boolean
  /** 'dark' renders the player for a navy ground (translucent card, white
   *  text, sky accents) — the default stays the light card. */
  tone?: 'light' | 'dark'
  className?: string
}

export const AudioBars: FC<AudioBarsProps> = ({ url, title, group, compact = false, tone = 'light', className }) => {
  const { t } = useTranslation()
  const reducedMotion = useAudioReducedMotion()
  const dark = tone === 'dark'
  // Start without crossOrigin so playback works even without CORS headers.
  // We add crossOrigin="anonymous" dynamically when setting up Web Audio.
  const { audioRef, status, toggle, isMounted, audioProps } = useAudioPlayer({
    url,
    group,
  })

  // Web Audio API refs
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationRef = useRef<number | null>(null)
  const [barHeights, setBarHeights] = useState<number[]>(IDLE_BAR_HEIGHTS)
  const [useCssFallback, setUseCssFallback] = useState(false)
  const preparingRef = useRef(false)
  const [isPreparing, setIsPreparing] = useState(false)

  // Responsive bar count
  const vizRef = useRef<HTMLSpanElement>(null)
  const [visibleBars, setVisibleBars] = useState(BAR_COUNT)

  useEffect(() => {
    const el = vizRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width
      // A skipped content-visibility subtree reports zero until revealed.
      // Keep its initial bars instead of rebuilding every offscreen player.
      if (w === 0) return
      setVisibleBars(Math.max(12, Math.min(BAR_COUNT, Math.floor(w / 7))))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [isMounted])

  const setupWebAudio = useCallback(async () => {
    if (audioCtxRef.current || !audioRef.current || useCssFallback) return
    try {
      // Probe CORS support before setting crossOrigin on the audio element,
      // because crossOrigin="anonymous" blocks playback entirely when CORS fails.
      const probe = await fetch(audioRef.current.src, { method: 'HEAD', mode: 'cors' }).catch(() => null)
      if (!audioRef.current) {
        return
      }
      if (!probe || !probe.ok) {
        setUseCssFallback(true)
        return
      }

      const audio = audioRef.current
      audio.crossOrigin = 'anonymous'
      // The player normally uses preload="none". This explicit play request
      // must start loading before we wait for canplay, or setup can stall.
      audio.preload = 'auto'
      audio.load()
      // Wait for the element to be ready after reload with crossOrigin
      await new Promise<void>((resolve, reject) => {
        const onReady = () => {
          audio.removeEventListener('canplay', onReady)
          audio.removeEventListener('error', onError)
          resolve()
        }
        const onError = () => {
          audio.removeEventListener('canplay', onReady)
          audio.removeEventListener('error', onError)
          reject(new Error('Audio load failed'))
        }
        audio.addEventListener('canplay', onReady, { once: true })
        audio.addEventListener('error', onError, { once: true })
      })
      if (audioRef.current !== audio) {
        return
      }

      const AudioContextClass =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new AudioContextClass()
      if (ctx.state === 'suspended') await ctx.resume()

      const analyser = ctx.createAnalyser()
      analyser.fftSize = 128
      analyser.smoothingTimeConstant = 0.8

      const source = ctx.createMediaElementSource(audio)
      source.connect(analyser)
      analyser.connect(ctx.destination)

      audioCtxRef.current = ctx
      analyserRef.current = analyser
    } catch {
      if (audioRef.current) {
        audioRef.current.crossOrigin = null
        audioRef.current.load()
      }
      setUseCssFallback(true)
    }
  }, [useCssFallback, audioRef])

  // Animation loop
  useEffect(() => {
    if (!status.isPlaying || !analyserRef.current || useCssFallback || reducedMotion) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      if (!status.isPlaying) setBarHeights(IDLE_BAR_HEIGHTS)
      return
    }

    const analyser = analyserRef.current
    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    let zeroCount = 0
    let hasReceivedData = false

    const animate = () => {
      analyser.getByteFrequencyData(dataArray)
      const sum = dataArray.reduce((a, b) => a + b, 0)

      if (sum === 0) {
        if (hasReceivedData) {
          zeroCount += 1
          if (zeroCount > 60) {
            setUseCssFallback(true)
            return
          }
        }
      } else {
        hasReceivedData = true
        zeroCount = 0
      }

      const sampleRate = audioCtxRef.current?.sampleRate || 44100
      const maxBinIndex = Math.floor((10000 / (sampleRate / 2)) * dataArray.length)

      const heights = Array.from({ length: BAR_COUNT }, (_, i) => {
        const idx = Math.floor((i / BAR_COUNT) * maxBinIndex)
        return Math.max(20, (dataArray[idx] / 255) * 100)
      })

      setBarHeights(heights)
      animationRef.current = requestAnimationFrame(animate)
    }

    animate()
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [status.isPlaying, useCssFallback, reducedMotion])

  // Cleanup Web Audio on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
      // close() throws InvalidStateError on an already-closed context (double
      // cleanup / a prior error-path close), and it returns a promise that can
      // reject on a state race — guard both. See HT-APP (Cannot close a closed
      // AudioContext).
      const ctx = audioCtxRef.current
      if (ctx && ctx.state !== 'closed') {
        void ctx.close().catch(() => {})
      }
    }
  }, [])

  if (!isMounted) return null

  const isPlaying = status.isPlaying
  const isLoading = isPreparing || status.isLoading
  const progress = status.progress

  const handlePlay = async (e: React.MouseEvent) => {
    e.stopPropagation()
    // Guard synchronously too: another activation can arrive before React
    // commits the disabled state, or while the CORS probe is still pending.
    if (preparingRef.current || status.isLoading) {
      return
    }
    if (isPlaying) {
      toggle()
      return
    }
    preparingRef.current = true
    setIsPreparing(true)
    try {
      if (!useCssFallback) {
        await setupWebAudio()
        if (audioCtxRef.current?.state === 'suspended') {
          await audioCtxRef.current.resume().catch(() => setUseCssFallback(true))
        }
      }
      if (audioRef.current) {
        toggle()
      }
    } finally {
      preparingRef.current = false
      setIsPreparing(false)
    }
  }

  const playButtonSize = compact ? 'w-8' : 'w-11'

  return (
    <button
      type="button"
      data-tracking="ignore"
      aria-label={`${isLoading ? t('audio.loading', 'Loading audio') : isPlaying ? t('audio.pause', 'Pause audio') : t('audio.play', 'Play audio')}${title ? ` – ${title}` : ''}`}
      aria-pressed={isPlaying}
      aria-busy={isLoading}
      disabled={isLoading}
      onClick={handlePlay}
      className={cx(
        'group focus-visible:outline-lapis relative w-full cursor-pointer overflow-hidden rounded-xl text-left outline-offset-4 transition-colors duration-300 disabled:cursor-wait focus-visible:outline-2',
        compact ? 'px-3 py-2.5' : 'px-4 py-3.5',
        dark
          ? isPlaying
            ? 'border-sky-80/40 bg-sky-80/10 border'
            : 'border border-white/15 bg-white/8 hover:border-white/30'
          : isPlaying
            ? 'border-lapis/25 bg-lapis/[0.03] border shadow-sm'
            : 'border border-slate-200 bg-white hover:border-slate-300',
        className
      )}
    >
      <span className="flex items-center gap-2.5">
        <span
          className={cx(
            'relative flex aspect-square shrink-0 items-center justify-center rounded-full',
            playButtonSize
          )}
        >
          <AudioProgressRing size={compact ? 32 : 44} progress={progress} className="absolute inset-0" />
          {/* Icon colors stay dark in both tones — the progress ring paints a
              white disc behind them. */}
          {isLoading ? (
            <span
              aria-hidden="true"
              className="border-navy/20 border-t-navy relative size-3.5 rounded-full border-2 motion-safe:animate-spin"
            />
          ) : isPlaying ? (
            <SvgPause aria-hidden className="text-lapis relative h-[11px] w-[11px]" />
          ) : (
            <SvgPlay aria-hidden className="text-navy relative h-[13px] w-[13px] translate-x-px" />
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span
            className={cx(
              'block truncate font-semibold tracking-tight',
              compact ? 'text-sm sm:text-xs' : 'text-sm',
              dark ? (isPlaying ? 'text-sky-80' : 'text-white') : isPlaying ? 'text-lapis' : 'text-navy'
            )}
          >
            {title}
          </span>

          <span
            ref={vizRef}
            className={cx(
              'flex w-full items-end gap-px transition-[height,margin] duration-300 ease-out',
              isPlaying ? 'mt-0.5' : 'mt-1.5'
            )}
            style={{ height: isPlaying ? (compact ? 28 : 36) : 8 }}
          >
            {useCssFallback
              ? BAR_DELAYS.slice(0, visibleBars).map((delay, i) => (
                  <span
                    key={i}
                    className={cx(
                      'flex-1 rounded-full transition-colors duration-300',
                      isPlaying
                        ? cx(
                            dark ? 'bg-sky-80' : 'bg-lapis',
                            'animate-[soundBar_0.8s_ease-in-out_infinite] motion-reduce:animate-none'
                          )
                        : dark
                          ? 'bg-white/20'
                          : 'bg-slate-200/60'
                    )}
                    style={{
                      height: isPlaying ? undefined : '100%',
                      animationDelay: isPlaying ? `-${delay}s` : undefined,
                    }}
                  />
                ))
              : barHeights
                  .slice(0, visibleBars)
                  .map((height, i) => (
                    <span
                      key={i}
                      className={cx(
                        'flex-1 rounded-full',
                        isPlaying ? (dark ? 'bg-sky-80' : 'bg-lapis') : dark ? 'bg-white/20' : 'bg-slate-200/60'
                      )}
                      style={{ height: isPlaying ? `${height}%` : '100%' }}
                    />
                  ))}
          </span>
        </span>
      </span>

      <span
        className={cx(
          'absolute bottom-0 left-0 h-[2px] transition-all duration-150',
          isPlaying ? (dark ? 'bg-sky-80' : 'bg-lapis') : 'bg-transparent'
        )}
        style={{ width: `${progress * 100}%` }}
      />

      {/* The named button owns playback. Explicitly hide the backing element
          so skipped offscreen styles cannot expose its native shadow controls. */}
      <audio ref={audioRef} {...audioProps} aria-hidden="true" tabIndex={-1} />
    </button>
  )
}
